import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export const REPORT_MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

const pad = (value) => String(value).padStart(2, '0');

export const getTodayDate = () => new Date().toISOString().split('T')[0];

export const getMonthEndDate = (year, month) => (
    `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`
);

export const getMonthDateRange = (year, month) => ({
    dateFrom: `${year}-${pad(month)}-01`,
    dateTo: getMonthEndDate(year, month),
});

export const getYearDateRange = (year) => ({
    dateFrom: `${year}-01-01`,
    dateTo: `${year}-12-31`,
});

export default function ReportPeriodSelector({
    mode,
    onModeChange,
    month,
    year,
    onMonthChange,
    onYearChange,
    dateFrom,
    dateTo,
    onDateFromChange,
    onDateToChange,
    asOfDate,
    onAsOfDateChange,
    customMode = 'range',
    title = 'Date Coverage',
    effectText = 'Posted source records inside this coverage are included in the report preview.',
    testId = 'report-period-selector',
}) {
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 12 }, (_, index) => currentYear + 1 - index);
    const isAsOfReport = customMode === 'single';
    const modes = [
        { id: 'monthly', label: 'Monthly', helper: isAsOfReport ? 'Month-end as-of date' : 'One accounting month' },
        { id: 'annual', label: 'Annual', helper: isAsOfReport ? 'Year-end as-of date' : 'Full accounting year' },
        { id: 'custom', label: 'Custom', helper: customMode === 'single' ? 'Exact as-of date' : 'Exact date range' },
    ];

    return (
        <section className="space-y-3" data-testid={testId}>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <Label className="text-sm font-semibold text-slate-800">{title}</Label>
                    <p className="text-xs text-slate-500 mt-1">{effectText}</p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    {modes.map(option => (
                        <button
                            key={option.id}
                            type="button"
                            onClick={() => onModeChange(option.id)}
                            className={`rounded-md border px-3 py-2 text-left transition-colors ${
                                mode === option.id
                                    ? 'border-[#2F8FCB] bg-[#E8F4FB] text-[#0F2A44] shadow-sm'
                                    : 'border-slate-200 bg-white text-slate-600 hover:border-[#2F8FCB]'
                            }`}
                            data-testid={`${testId}-${option.id}`}
                        >
                            <span className="block text-sm font-semibold">{option.label}</span>
                            <span className="block text-[11px] leading-tight">{option.helper}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {mode === 'monthly' && (
                    <div>
                        <Label className="text-sm">Month</Label>
                        <Select value={String(month)} onValueChange={(value) => onMonthChange(parseInt(value, 10))}>
                            <SelectTrigger className="mt-1" data-testid={`${testId}-month`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {REPORT_MONTHS.map(item => (
                                    <SelectItem key={item.value} value={String(item.value)}>{item.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {(mode === 'monthly' || mode === 'annual') && (
                    <div>
                        <Label className="text-sm">Year</Label>
                        <Select value={String(year)} onValueChange={(value) => onYearChange(parseInt(value, 10))}>
                            <SelectTrigger className="mt-1" data-testid={`${testId}-year`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {yearOptions.map(item => (
                                    <SelectItem key={item} value={String(item)}>{item}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {mode === 'custom' && customMode === 'single' && (
                    <div>
                        <Label className="text-sm">As Of Date</Label>
                        <Input
                            type="date"
                            value={asOfDate}
                            onChange={(event) => onAsOfDateChange(event.target.value)}
                            className="mt-1"
                            data-testid={`${testId}-as-of-date`}
                        />
                    </div>
                )}

                {mode === 'custom' && customMode === 'range' && (
                    <>
                        <div>
                            <Label className="text-sm">From Date</Label>
                            <Input
                                type="date"
                                value={dateFrom}
                                onChange={(event) => onDateFromChange(event.target.value)}
                                className="mt-1"
                                data-testid={`${testId}-date-from`}
                            />
                        </div>
                        <div>
                            <Label className="text-sm">To Date</Label>
                            <Input
                                type="date"
                                value={dateTo}
                                onChange={(event) => onDateToChange(event.target.value)}
                                className="mt-1"
                                data-testid={`${testId}-date-to`}
                            />
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
